import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Body, {
  getVideoIDAndPlaceholder,
} from '@plone/volto/components/manage/Blocks/Video/Body';
import { withBlockExtensions } from '@plone/volto/helpers/Extensions';
import { flattenToAppURL, isInternalURL } from '@plone/volto/helpers/Url/Url';
import { VIDEO_FORMATO_VERTICAL } from './schema';

/** Mesma resolução que o `Body.jsx` do core faz com `data.preview_image`. */
const resolvePreviewImage = (previewImage) => {
  if (!previewImage) return null;
  return isInternalURL(previewImage)
    ? `${flattenToAppURL(previewImage)}/@@images/image`
    : previewImage;
};

/**
 * Se a capa escolhida não carregar, deixa de usá-la.
 *
 * O `Body.jsx` do core dá prioridade absoluta ao `preview_image` e só cai na
 * thumbnail do YouTube/Vimeo quando o campo está VAZIO — se ele estiver
 * preenchido com algo que não é imagem (uma URL de página, por exemplo), o
 * player fica com um retângulo cinza e a thumbnail perfeitamente disponível
 * nunca é usada.
 *
 * A verificação é no cliente e só roda quando há thumbnail para onde cair, de
 * modo que a capa continua sendo a prioridade sempre que ela funciona — isto
 * é degradação graciosa, não substituição silenciosa.
 */
const useCapaUtilizavel = (previewImage, thumbnailURL) => {
  const [quebrada, setQuebrada] = React.useState(false);

  React.useEffect(() => {
    setQuebrada(false);

    const src = resolvePreviewImage(previewImage);
    if (!src || !thumbnailURL || typeof window === 'undefined') {
      return undefined;
    }

    let vivo = true;
    const img = new window.Image();
    img.onerror = () => {
      if (vivo) setQuebrada(true);
    };
    img.src = src;

    return () => {
      vivo = false;
    };
  }, [previewImage, thumbnailURL]);

  return !quebrada;
};

/**
 * Bloco Vídeo com legenda.
 *
 * O core não tem campo de legenda; o design da sub-home pede um texto logo
 * abaixo do player ("Legenda da imagem/vídeo"), no mesmo desenho que o bloco
 * Imagem já usa. Por isso o `<div>` do core vira `<figure>` e ganha um
 * `<figcaption>` com a MESMA classe do bloco Imagem
 * (`.mpf-image-caption`, em `components/_image-block.scss`), para as duas
 * legendas não divergirem.
 *
 * O `<Body>` do core segue responsável pelo player em si. O que ele aceita
 * hoje, sem alteração nossa: YouTube (vídeo, `youtu.be`, `live` e playlist
 * por `list=`), Vimeo e arquivos `.mp4` — externos ou do próprio portal, caso
 * em que a URL interna é resolvida para `.../@@download/file`. Formatos de
 * streaming adaptativo (HLS/`.m3u8`, DASH) e outros contêineres (`.webm`,
 * `.ogv`) NÃO são reconhecidos e caem no aviso de formato inválido.
 */
export const View = ({ className, data, style }) => {
  const { thumbnailURL } = getVideoIDAndPlaceholder(data.url);
  const capaUtilizavel = useCapaUtilizavel(data.preview_image, thumbnailURL);
  const dadosDoPlayer = capaUtilizavel
    ? data
    : { ...data, preview_image: undefined };

  return (
    <figure
      className={cx(
        'block video align mpf-video-block',
        { center: !Boolean(data.align) },
        // Vídeo de celular: player 9:16, estreito e centralizado.
        {
          'mpf-video-block--vertical': data.formato === VIDEO_FORMATO_VERTICAL,
        },
        data.align,
        className,
      )}
      style={style}
    >
      <Body data={dadosDoPlayer} />

      {data.caption && (
        <figcaption className="mpf-image-caption">{data.caption}</figcaption>
      )}
    </figure>
  );
};

View.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default withBlockExtensions(View);
